import { useEffect, useState } from 'react';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';

/**
 * The section of the map the user is currently looking at, so panning somewhere else and printing
 * again prints there. Framing is approximate by design — it tracks where you are, not the exact
 * rectangle at the instant the modal opens.
 * Unprojects the container's corners inset by the map's padding rather than reading getBounds():
 * explore reserves a wide left padding for the results sidebar, and getBounds() would hand back the
 * region hidden underneath it. Unproject also follows the camera, so a rotated map needs no
 * special case — the four corners collapse to the extremes the print has to cover.
 */
const usePrintViewportBounds = (enabled: boolean): AllTrailsBounds | null => {
  const map = useMap();
  const [bounds, setBounds] = useState<AllTrailsBounds | null>(null);

  useEffect(() => {
    if (!enabled || !map) {
      return undefined;
    }

    const updateBounds = () => {
      const { offsetHeight, offsetWidth } = map.getContainer();
      const { bottom = 0, left = 0, right = 0, top = 0 } = map.getPadding();
      const corners = [
        map.unproject([left, top]),
        map.unproject([offsetWidth - right, top]),
        map.unproject([left, offsetHeight - bottom]),
        map.unproject([offsetWidth - right, offsetHeight - bottom])
      ];
      const latitudes = corners.map(({ lat }) => lat);
      const longitudes = corners.map(({ lng }) => lng);

      setBounds({
        latitudeTopLeft: Math.max(...latitudes),
        longitudeTopLeft: Math.min(...longitudes),
        latitudeBottomRight: Math.min(...latitudes),
        longitudeBottomRight: Math.max(...longitudes)
      });
    };

    updateBounds();
    map.on('moveend', updateBounds);
    map.on('resize', updateBounds);
    return () => {
      map.off('moveend', updateBounds);
      map.off('resize', updateBounds);
    };
  }, [enabled, map]);

  return enabled ? bounds : null;
};

export default usePrintViewportBounds;
